/* eslint-disable i18next/no-literal-string */
import { Dayjs } from 'date';
import { ComponentProps, forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DateRangePicker, FormControl, FormHelperText } from 'ui';

export type DateRangeFieldValue = {
  from: Dayjs;
  to: Dayjs;
};

export type DateRangeFieldProps = {
  name: string;
  required?: boolean;
  helperText?: string;
  defaultValue: DateRangeFieldValue;
  onChange?: (value: DateRangeFieldValue) => void;
  onAccept?: () => void;
} & Omit<ComponentProps<typeof DateRangePicker>, 'onChange' | 'onAccept' | 'defaultValue' | 'defaultValues'>;

export const DateRangeField = forwardRef<HTMLDivElement, DateRangeFieldProps>(function DateRangeField(
  { name, required = false, helperText, defaultValue, onChange, onAccept, ...rest },
  ref
) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      rules={
        required
          ? {
              validate: (value: DateRangeFieldValue | undefined) =>
                value?.from && value?.to ? true : 'This field is required',
            }
          : {}
      }
      render={({ field, formState }) => {
        const error = formState.errors[name];

        return (
          <FormControl required={required} error={Boolean(error)} ref={ref}>
            <DateRangePicker
              {...rest}
              defaultValues={defaultValue}
              onAccept={onAccept}
              onChange={(value) => {
                field.onChange(value);
                onChange?.(value);
              }}
            />
            {error || helperText ? (
              <FormHelperText>{error ? String(error?.message) : helperText}</FormHelperText>
            ) : null}
          </FormControl>
        );
      }}
    />
  );
});
